<template>
	<CardGeneric class="text-left max-sm:pb-3 shippingInfoComponent">
		<p class="cardTitle">{{ $t("contactAndShippingTitle") }}</p>
		<p class="cardSubTitle">{{ $t("contactAndShippingSubtitle") }}</p>

		<div class="grid grid-cols-2 gap-y-5 gap-x-7">
			<FormTextField
				id="shippingFirstName"
				:label="$t('shippingFirstName')"
				class="max-sm:col-span-2"
				required
				:has-error="!!firstNameInvalid"
				:error-message="$t('invalidFirstName')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingFirstName"
				:key="shippingFirstName"
				@blur="
					({ value }) => {
						cartStore.setShippingFirstName(value)
						validateFirstName()
					}
				" />

			<FormTextField
				id="shippingLastName"
				:label="$t('shippingLastName')"
				class="max-sm:col-span-2"
				required
				:has-error="!!lastNameInvalid"
				:error-message="$t('invalidLastName')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingLastName"
				:key="shippingLastName"
				@blur="
					({ value }) => {
						cartStore.setShippingLastName(value)
						validateLastName()
					}
				" />

			<FormTextField
				id="shippingEmail"
				:label="$t('shippingEmail')"
				class="max-sm:col-span-2"
				required
				:has-error="!!emailsAreInvalid"
				:error-message="$t('invalidEmail')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingEmail"
				:key="shippingEmail"
				@blur="
					({ value }) => {
						cartStore.setShippingEmail(value)
						termsStore.setEmail(value)
						validateEmail()
					}
				" />

			<FormTextField
				id="shippingConfirmEmail"
				:label="$t('shippingEmailConfirm')"
				class="max-sm:col-span-2"
				required
				:has-error="!!emailsAreInvalid"
				:error-message="$t('invalidEmail')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingEmailConfirm"
				:key="shippingEmailConfirm"
				@blur="
					({ value }) => {
						cartStore.setShippingEmailConfirm(value)
						validateEmail()
					}
				" />

			<FormPhoneField
				id="shippingPhone"
				:label="$t('shippingPhone')"
				class="max-sm:col-span-2"
				required
				:has-error="!!phoneInvalid"
				:error-message="$t('invalidPhone')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingPhone"
				:key="shippingPhone"
				@blur="
					({ value }) => {
						cartStore.setShippingPhone(value)
						validatePhone()
					}
				" />

			<FormTextField
				id="shippingAddress1"
				v-if="enableAdvancedShippingInfoFields"
				:label="$t('shippingAddress1')"
				class="col-span-2"
				required
				:has-error="!!address1Invalid"
				:error-message="$t('invalidAddress1')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingAddress1"
				:key="shippingAddress1"
				@changed="
					({ value }) => {
						cartStore.setShippingAddress1(value)
						validateAddress1()
					}
				" />

			<FormTextField
				id="shippingAddress2"
				v-if="enableAdvancedShippingInfoFields"
				:label="$t('shippingAddress2')"
				class="col-span-2"
				:initialValue="shippingAddress2"
				:key="shippingAddress2"
				:disabled="shouldDisableCheckout"
				@changed="({ value }) => cartStore.setShippingAddress2(value)" />

			<FormDropdown
				id="shippingCountry"
				v-if="enableAdvancedShippingInfoFields"
				:label="$t('shippingCountry')"
				class="max-sm:col-span-2"
				required
				:has-error="!!countryInvalid"
				:error-message="$t('invalidCountry')"
				:disabled="shouldDisableCheckout"
				:options="countries"
				:initialValue="shippingCountry"
				:key="shippingCountry"
				@blur="validateCountry"
				@changed="handleCountryChanged" />

			<FormDropdown
				id="shippingState"
				v-if="enableAdvancedShippingInfoFields"
				:label="shippingCountry === 'CAN' ? $t('shippingProvince') : $t('shippingState')"
				class="max-sm:col-span-2"
				:required="states.length > 0"
				:has-error="!!stateInvalid"
				:error-message="shippingCountry === 'CAN' ? $t('invalidProvince') : $t('invalidState')"
				:disabled="shouldDisableCheckout || !shippingCountry || states.length <= 0"
				:options="states"
				:initialValue="shippingState"
				:key="shippingCountry + shippingState + states.length"
				@blur="validateState"
				@changed="
					({ value }) => {
						cartStore.setShippingState(value)
						validateCountry()
					}
				" />

			<FormTextField
				id="shippingCity"
				v-if="enableAdvancedShippingInfoFields"
				:label="$t('shippingCity')"
				class="max-sm:col-span-2"
				required
				:has-error="!!cityInvalid"
				:error-message="$t('invalidCity')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingCity"
				:key="shippingCity"
				@changed="
					({ value }) => {
						cartStore.setShippingCity(value)
						validateCity()
					}
				" />

			<FormZipCodeField
				id="shippingZipcode"
				v-if="enableAdvancedShippingInfoFields"
				:label="shippingCountry === 'CAN' ? $t('shippingPostalCode') : $t('shippingZipcode')"
				class="max-sm:col-span-2"
				required
				:has-error="!!zipcodeInvalid"
				:error-message="shippingCountry === 'CAN' ? $t('invalidPostalCode') : $t('invalidZipcode')"
				:disabled="shouldDisableCheckout"
				:initialValue="shippingZipcode"
				:country="shippingCountry"
				:key="shippingCountry"
				@changed="
					({ value }) => {
						cartStore.setShippingZipcode(value)
						validateZipCode()
					}
				"
				ref="shippingZipcodeRef" />

			<!--
			<FormCheckboxWithLabel
				id="newsAndUpdates"
				:label="$t('newsAndUpdates', { eventName: runtimeConfig.public.EVENT_NAME })"
				class="col-span-2"
				required
				:initialValue="cartStore.newsAndUpdates"
				:key="cartStore.newsAndUpdates"
				@changed="({ value }) => cartStore.setNewsAndUpdates(value)" />-->
		</div>
	</CardGeneric>
</template>

<script setup>
	import { useCartStore } from "~/stores/CartStore"
	import { useRefsStore } from "~/stores/RefsStore"
	import { isEmailFormat, isNonEmpty } from "~/assets/helpers/fieldValidators"
	import { UPDATE_BILLING_COUNTRY, UPDATE_SHIPPING_COUNTRY } from "assets/events/cart.js"

	const { $countryAndStatesService, $cartService, $eventBus, $currency, $configs } = useNuxtApp()
	const runtimeConfig = useRuntimeConfig()
	const isCheckoutDisabled = runtimeConfig.public.IS_CHECKOUT_DISABLED
	const shouldDisableCheckout = $configs.$isConfigEnabled(isCheckoutDisabled)
	const enableAdvancedShippingInfoFields = Boolean(runtimeConfig.public.ENABLE_DETAILED_SHIPPING_INFO)

	const cartStore = useCartStore()
	const termsStore = useTermsStore()
	const refsStore = useRefsStore()

	const firstNameInvalid = ref(false)
	const lastNameInvalid = ref(false)
	const emailsAreInvalid = ref(false)
	const phoneInvalid = ref(false)
	const address1Invalid = ref(false)
	const countryInvalid = ref(false)
	const stateInvalid = ref(false)
	const cityInvalid = ref(false)
	const zipcodeInvalid = ref(false)
	const shippingZipcodeRef = ref(null)

	const {
		shippingFirstName,
		shippingLastName,
		shippingPhone,
		shippingEmail,
		shippingEmailConfirm,
		shippingAddress1,
		shippingAddress2,
		shippingCountry,
		shippingState,
		shippingCity,
		shippingZipcode,
		billingAndShippingAreSame,
	} = storeToRefs(cartStore)

	const states = ref([])
	const countries = $countryAndStatesService.$getSupportedCountries()
	const desiredCountryToSwitchTo = ref(null)

	const handleCountryChanged = ({ value }) => {
		if (billingAndShippingAreSame.value) {
			// Attempt to update the currency before allowing the user to switch their country, if the currency call fails, then the shipping country should not change.
			const iso3 = value
			desiredCountryToSwitchTo.value = iso3

			const currency = $currency.$getCurrencyByCountryIso3(iso3)
			$cartService.$updateCartCurrency(currency.code)
			//Look for UPDATE_BILLING_COUNTRY event handler
		} else {
			//Populate the list of states available depending on the country selected
			const iso3 = value
			cartStore.setShippingCountry(iso3)
			cartStore.setShippingState("")
			cartStore.setShippingZipcode("")
			states.value = $countryAndStatesService.$getStatesForCountryByIsoCode(iso3)
		}
	}

	const validateFirstName = () => {
		firstNameInvalid.value = !isNonEmpty(shippingFirstName.value)
	}

	const validateLastName = () => {
		lastNameInvalid.value = !isNonEmpty(shippingLastName.value)
	}

	const validateEmail = () => {
		emailsAreInvalid.value =
			!isNonEmpty(shippingEmail.value) ||
			!isNonEmpty(shippingEmailConfirm.value) ||
			!cartStore.areEmailsTheSame() ||
			!isEmailFormat(shippingEmail.value) ||
			!isEmailFormat(shippingEmailConfirm.value)
	}

	const validatePhone = () => {
		phoneInvalid.value = !isNonEmpty(shippingPhone.value)
	}

	const validateAddress1 = () => {
		address1Invalid.value = !isNonEmpty(shippingAddress1.value)
	}

	const validateCountry = () => {
		countryInvalid.value = !isNonEmpty(shippingCountry.value)
	}

	const validateState = () => {
		stateInvalid.value = states.value.length > 0 ? !isNonEmpty(shippingState.value) : false
	}

	const validateCity = () => {
		cityInvalid.value = !isNonEmpty(shippingCity.value)
	}

	const validateZipCode = () => {
		zipcodeInvalid.value = !shippingZipcodeRef.value?.isFieldValid()
	}

	//Called when the user clicks checkout (checkout.vue)
	const isShippingInfoValid = () => {
		validateFirstName()
		validateLastName()
		validateEmail()
		validatePhone()

		let areAdvancedShippingFieldsValid = true
		if (enableAdvancedShippingInfoFields) {
			validateAddress1()
			validateCountry()
			validateState()
			validateCity()
			validateZipCode()

			areAdvancedShippingFieldsValid =
				!address1Invalid.value &&
				!countryInvalid.value &&
				!stateInvalid.value &&
				!cityInvalid.value &&
				!zipcodeInvalid.value
		}

		return (
			!firstNameInvalid.value &&
			!lastNameInvalid.value &&
			!emailsAreInvalid.value &&
			!phoneInvalid.value &&
			areAdvancedShippingFieldsValid
		)
	}

	onMounted(() => {
		//Populate the states if the page was refreshed with a country selected
		if (shippingCountry.value) {
			states.value = $countryAndStatesService.$getStatesForCountryByIsoCode(shippingCountry.value)
		}

		$eventBus.$on(UPDATE_BILLING_COUNTRY, () => {
			if (billingAndShippingAreSame.value && desiredCountryToSwitchTo.value) {
				countryInvalid.value = !isNonEmpty(desiredCountryToSwitchTo.value)
				stateInvalid.value = false

				cartStore.setShippingCountry(desiredCountryToSwitchTo.value)
				cartStore.setShippingState("")
				states.value = $countryAndStatesService.$getStatesForCountryByIsoCode(desiredCountryToSwitchTo.value)

				//set desiredCountryToSwitchTo to null, otherwise a currency switch will trigger this method
				desiredCountryToSwitchTo.value = null
			}
		})

		$eventBus.$on(UPDATE_SHIPPING_COUNTRY, () => {
			states.value = $countryAndStatesService.$getStatesForCountryByIsoCode(shippingCountry.value)
			countryInvalid.value = !isNonEmpty(shippingCountry.value)
			stateInvalid.value = false
		})
	})

	onUnmounted(() => {
		$eventBus.$off(UPDATE_BILLING_COUNTRY)
		$eventBus.$off(UPDATE_SHIPPING_COUNTRY)
	})

	defineExpose({
		isShippingInfoValid,
	})
</script>

<style scoped></style>
